import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ShieldCheck,
  Building2,
  Wallet,
  Package,
  Image,
  Calendar,
  FileText,
  FileSignature,
  ArrowRight,
  BarChart3,
  Loader2,
  Globe,
  Lock,
  Link2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useVendorGettingStarted from '../../hooks/useVendorGettingStarted';
import ProductHome from '../../components/shared/product/ProductHome';
import VendorScheduleAlerts from '../../components/vendor/VendorScheduleAlerts';
import api from '../../services/authService';
import { vendorExternalLeadApi } from '../../services/vendorExternalLeadService';
import ChannelBadge from '../../components/shared/ChannelBadge';

const ICON_BY_ID = {
  verify: ShieldCheck,
  business: Building2,
  connect: Wallet,
  services: Package,
  portfolio: Image,
  ready: Calendar,
};

const EXTRA_CARDS = [
  {
    id: 'invoices',
    title: 'Send a guest invoice',
    description: 'Bill external clients with branded invoices, discounts, and PDF export.',
    href: '/vendor/invoices',
    cta: 'External invoices',
    icon: FileText,
  },
  {
    id: 'contracts',
    title: 'Send a contract to sign',
    description: 'Use templates or AI drafts, then collect a client signature online.',
    href: '/vendor/contracts',
    cta: 'Open contracts',
    icon: FileSignature,
  },
];

const VendorHome = () => {
  const { user } = useAuth();
  const { steps, doneCount, allDone, isLoading, visible, dismiss, restore } =
    useVendorGettingStarted();
  const businessName =
    user?.vendor_profile?.business_name ||
    user?.name?.split(' ')?.[0] ||
    'your business';

  const { data: statsData } = useQuery({
    queryKey: ['vendorBookingStats'],
    queryFn: async () => {
      const response = await api.get('/vendor/bookings/statistics');
      return response.data;
    },
  });

  const { data: leadsData } = useQuery({
    queryKey: ['vendorExternalLeads', { status: 'new', per_page: 1 }],
    queryFn: () => vendorExternalLeadApi.listLeads({ status: 'new', per_page: 1 }),
  });

  const stats = statsData?.data || {};
  const newLeads = leadsData?.counts?.new ?? 0;

  const cards = useMemo(() => {
    const setupCards = steps.map((step) => ({
      id: step.id,
      title: step.title,
      description: step.description,
      href: step.href,
      cta: step.cta,
      done: step.done,
      icon: ICON_BY_ID[step.id] || Package,
    }));
    return [...setupCards, ...EXTRA_CARDS];
  }, [steps]);

  const quickLinks = [
    { label: 'Internal dashboard', href: '/vendor/dashboard' },
    { label: 'External overview', href: '/vendor/external' },
    { label: 'Bookings', href: '/vendor/bookings' },
    { label: 'Services', href: '/vendor/services' },
    { label: 'Account', href: '/vendor/account' },
  ];

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-ink" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="mx-auto max-w-5xl space-y-4 px-1 pt-2 sm:pt-4">
        <VendorScheduleAlerts />

        <div>
          <p className="mb-2 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-tertiary">
            Your workspace
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Link
              to="/vendor/dashboard"
              className="group rounded-lg border border-border bg-surface p-5 transition-colors hover:border-ink/30 hover:bg-surface-muted"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-ink-tertiary" />
                  <span className="text-sm font-semibold text-ink">Internal</span>
                </div>
                <ChannelBadge channel="internal" />
              </div>
              <p className="text-2xl font-semibold tabular-nums text-ink">
                {stats.platform_bookings ?? 0}
              </p>
              <p className="mt-1 text-xs text-ink-secondary">
                Marketplace bookings · Accounting & payouts
              </p>
              <p className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-ink">
                Open Internal dashboard
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </p>
            </Link>

            <Link
              to="/vendor/external"
              className="group rounded-lg border border-border bg-surface p-5 transition-colors hover:border-ink/30 hover:bg-surface-muted"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-ink-tertiary" />
                  <span className="text-sm font-semibold text-ink">External</span>
                </div>
                <ChannelBadge channel="external" />
              </div>
              <p className="text-2xl font-semibold tabular-nums text-ink">
                {stats.private_bookings ?? 0}
              </p>
              <p className="mt-1 text-xs text-ink-secondary">
                Private bookings
                {newLeads > 0 ? ` · ${newLeads} new lead${newLeads === 1 ? '' : 's'}` : ''}
                {' · '}outside marketplace fees
              </p>
              <p className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-ink">
                Open External overview
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </p>
            </Link>
          </div>

          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <Link
              to="/vendor/external-leads"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
            >
              <Link2 className="h-3.5 w-3.5 text-ink-tertiary" />
              External leads
            </Link>
            <Link
              to="/vendor/invoices"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
            >
              <FileText className="h-3.5 w-3.5 text-ink-tertiary" />
              Guest invoices
            </Link>
            <Link
              to="/vendor/internal-invoices"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
            >
              <FileText className="h-3.5 w-3.5 text-ink-tertiary" />
              Platform invoices
            </Link>
          </div>
        </div>
      </div>

      <ProductHome
        brandLabel="vendaList"
        entityLabel={businessName}
        subtitle={
          allDone
            ? 'You’re set up — pick Internal for marketplace work or External for your own clients.'
            : 'Finish these steps to accept marketplace bookings and get paid.'
        }
        cards={cards}
        quickLinks={quickLinks}
        progress={visible ? { done: doneCount, total: steps.length } : null}
        checklistVisible={visible}
        onDismissChecklist={dismiss}
        onRestoreChecklist={restore}
        footer={
          <div className="flex flex-col items-center justify-between gap-3 rounded-lg border border-border bg-surface px-4 py-3 sm:flex-row">
            <div>
              <p className="text-sm font-medium text-ink">Need marketplace metrics?</p>
              <p className="text-xs text-ink-secondary">
                Earnings, recent Internal bookings, and performance live on the Internal dashboard.
              </p>
            </div>
            <Link
              to="/vendor/dashboard"
              className="inline-flex h-8 min-h-8 items-center gap-1.5 rounded-md bg-ink px-3 text-xs font-medium text-white hover:bg-neutral-800"
            >
              <BarChart3 className="h-3.5 w-3.5" />
              Internal dashboard
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        }
      />
    </div>
  );
};

export default VendorHome;
